import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import Toast from "cogo-toast";
import MapSvg from "./MapSvg";
import TurnstileActionPanel from "./TurnstileActionPanel";
import {apiGetMap, apiGetTurnstileDetail, apiSendCommand, apiOpenLegacy, apiGetCommands, apiGetZones} from "./Actions";
import {COMMAND, TURNSTILE_TYPE, usesLegacyOpen, commandErrorMessage} from "./mapConstants";
import PageHeader from "../components/PageHeader";

const MapView = () => {
    const navigate = useNavigate();
    const user = useSelector((s) => s.user);
    const subjectId = user?.activeAreal?.id;
    const canEdit = !!user?.user?.rights?.enigooTurniketsEdit;
    const [svg, setSvg] = useState("");
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null); // {turnstile} | {unknownId}
    const [commands, setCommands] = useState(null); // null = historii neukazovat (ne-socket)
    const [zones, setZones] = useState({}); // {zoneId: name}

    useEffect(() => {
        if (!subjectId) return;
        setLoading(true);
        apiGetMap(subjectId,
            (data) => { setSvg(data?.svg || ""); setLoading(false); },
            () => { Toast.error("Nepodařilo se načíst mapu."); setLoading(false); });
        // zóny jednou pro překlad id → název
        apiGetZones(subjectId, (d) => {
            const list = Array.isArray(d) ? d : (d?.zone || []);
            const map = {};
            list.forEach((z) => { if (z && z.id != null) map[z.id] = z.name; });
            setZones(map);
        }, () => {});
    }, [subjectId]);

    const loadCommands = (turnstileId) => {
        apiGetCommands(subjectId, turnstileId,
            (d) => setCommands(Array.isArray(d) ? d : []),
            () => setCommands([]));
    };

    const onMarkerClick = (turnstileId) => {
        setCommands(null);
        apiGetTurnstileDetail(subjectId, turnstileId,
            (t) => {
                if (!t) { setSelected({unknownId: turnstileId}); return; }
                setSelected({turnstile: t});
                // historii mají jen socket validatory
                if (t.turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET) loadCommands(t.id);
            },
            () => setSelected({unknownId: turnstileId}));
    };

    // payload: {} pro příkazy bez formuláře (TurnstileActionPanel je posílá rovnou),
    // jinak sestavený CommandFormModalem. onDone(success) řídí jeho spinner/zavření.
    const onAction = (code, payload = {}, onDone) => {
        const t = selected?.turnstile;
        if (!t) { onDone && onDone(false); return; }
        if (!canEdit) {
            Toast.error("Nemáte oprávnění k ovládání turniketů.");
            onDone && onDone(false);
            return;
        }
        const isSocket = t.turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET;
        const ok = () => {
            Toast.success("Příkaz odeslán.");
            if (isSocket) loadCommands(t.id);
            onDone && onDone(true);
        };
        const fail = (errCode) => {
            Toast.error(commandErrorMessage(errCode));
            onDone && onDone(false);
        };
        if (code === COMMAND.OPEN && usesLegacyOpen(t.turnstileType)) {
            apiOpenLegacy(subjectId, t.id, ok, fail);
        } else {
            apiSendCommand(subjectId, t.id, code, payload, ok, fail);
        }
    };

    const selectedZoneName = selected?.turnstile && selected.turnstile.zone != null
        ? zones[selected.turnstile.zone]
        : null;

    return (
        <div>
            <PageHeader crumbs={[{label: "Mapa turniketů"}]}/>
            {canEdit && (
                <Link to="/turnstile-map/editor" className="text-blue-600 text-sm">Editovat mapu</Link>
            )}
            <div style={{display: "flex", gap: "12px", height: "70vh"}}>
                <div style={{flex: 1, border: "1px solid #e5e7eb", borderRadius: 8}}>
                    {loading ? <p className="p-4">Načítám…</p> :
                        svg ? <MapSvg svg={svg} onMarkerClick={onMarkerClick}/> :
                            <p className="p-4">Mapa zatím není vytvořená. Vytvoř ji v editoru.</p>}
                </div>
                {selected && (
                    <div style={{width: 320, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "auto"}}>
                        <TurnstileActionPanel
                            turnstile={selected.turnstile}
                            unknownId={selected.unknownId}
                            onAction={onAction}
                            onDetail={selected.turnstile ? () => navigate("/turnstile/detail/" + selected.turnstile.id) : undefined}
                            commands={commands}
                            zoneName={selectedZoneName}/>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapView;
